import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Send, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import colors from '../config/colors';

const ContributePage = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [ratings, setRatings] = useState({
    academic_rigour: null,
    pastoral_care: null,
    diversity: null,
    cocurricular: null,
    teacher_retention: null,
    university_pathway: null,
    value_for_money: null,
    parent_communication: null,
    curriculum_quality: null,
    language_support: null
  });
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [detailedComments, setDetailedComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Attribute labels
  const attributeLabels = {
    academic_rigour: { label: 'Academic Rigour', description: 'Quality of teaching and academic standards' },
    pastoral_care: { label: 'Pastoral Care', description: 'How well the school supports student wellbeing' },
    diversity: { label: 'Diversity', description: 'International and cultural diversity' },
    cocurricular: { label: 'Co-curricular', description: 'Quality and variety of activities beyond classroom' },
    teacher_retention: { label: 'Teacher Retention', description: 'Teacher stability and experience' },
    university_pathway: { label: 'University Pathway', description: 'University guidance and placement track record' },
    value_for_money: { label: 'Value for Money', description: 'Fees relative to quality delivered' },
    parent_communication: { label: 'Parent Communication', description: 'How well school keeps parents informed' },
    curriculum_quality: { label: 'Curriculum Quality', description: 'Curriculum design and implementation' },
    language_support: { label: 'Language Support', description: 'Language development opportunities' }
  };

  const loadSchools = useCallback(async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/schools/list`);
      if (response.ok) {
        const data = await response.json();
        setSchools(data.schools || []);
      }
    } catch (error) {
      console.error('Failed to load schools:', error);
      setSchools([]);
    }
  }, []);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  const handleRatingClick = (attribute, value) => {
    setRatings(prev => ({ ...prev, [attribute]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSchoolId) {
      setError('Please select a school');
      return;
    }

    // At least one rating is required
    const hasRatings = Object.values(ratings).some(r => r !== null);
    if (!hasRatings) {
      setError('Please provide at least one rating');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const selectedSchool = schools.find(s => s.id === selectedSchoolId);
      
      // Convert 1-5 star ratings to 0-1 scale
      const normalizedRatings = {};
      Object.entries(ratings).forEach(([key, value]) => {
        if (value !== null) {
          normalizedRatings[key] = value / 5.0;
        }
      });

      const payload = {
        school_id: selectedSchoolId,
        school_name: selectedSchool?.full_name || 'Unknown',
        ...normalizedRatings,
        would_recommend: wouldRecommend,
        detailed_comments: detailedComments || null
      };

      const response = await fetch(`${BACKEND_URL}/api/feedback/detailed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitted(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setSelectedSchoolId('');
          setRatings({
            academic_rigour: null,
            pastoral_care: null,
            diversity: null,
            cocurricular: null,
            teacher_retention: null,
            university_pathway: null,
            value_for_money: null,
            parent_communication: null,
            curriculum_quality: null,
            language_support: null
          });
          setWouldRecommend(null);
          setDetailedComments('');
          setSubmitted(false);
        }, 3000);
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Feedback submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange }) => {
    const [hover, setHover] = useState(null);

    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={28}
            fill={star <= (hover || value) ? colors.accent.coral : 'none'}
            stroke={star <= (hover || value) ? colors.accent.coral : colors.border.medium}
            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(null)}
            onClick={() => onChange(star)}
          />
        ))}
      </div>
    );
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.background.main,
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              color: colors.text.secondary,
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '16px'
            }}
          >
            <ArrowLeft size={18} />
            Back
          </button>
          
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            color: colors.text.primary,
            marginBottom: '8px'
          }}>
            Share Your School Experience
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: colors.text.secondary,
            lineHeight: '1.6'
          }}>
            Your detailed feedback helps future parents make better decisions. All submissions are anonymous.
          </p>
        </div>

        {/* Success Message */}
        {submitted && (
          <div style={{ 
            padding: '20px',
            background: '#E8F5E9',
            borderRadius: '12px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <CheckCircle size={24} color={colors.accent.green} />
            <span style={{ fontWeight: '600', color: colors.accent.green }}>
              Thank you! Your feedback has been submitted successfully.
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ 
            background: colors.background.white,
            borderRadius: '12px',
            padding: '32px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            {/* School Selection */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: colors.text.primary,
                marginBottom: '12px'
              }}>
                Which school would you like to review? *
              </label>
              <select
                value={selectedSchoolId}
                onChange={(e) => setSelectedSchoolId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border.light}`,
                  background: colors.background.white,
                  cursor: 'pointer'
                }}
                required
              >
                <option value="">Select a school...</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.full_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Ratings */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ 
                fontSize: '18px',
                fontWeight: '600',
                color: colors.text.primary,
                marginBottom: '20px'
              }}>
                Rate the following aspects
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {Object.entries(attributeLabels).map(([key, { label, description }]) => (
                  <div key={key} style={{ 
                    paddingBottom: '20px',
                    borderBottom: `1px solid ${colors.border.light}`
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ 
                        fontSize: '15px',
                        fontWeight: '600',
                        color: colors.text.primary,
                        marginBottom: '4px'
                      }}>
                        {label}
                      </div>
                      <div style={{ 
                        fontSize: '13px',
                        color: colors.text.secondary
                      }}>
                        {description}
                      </div>
                    </div>
                    <StarRating 
                      value={ratings[key]} 
                      onChange={(value) => handleRatingClick(key, value)} 
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendation */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: colors.text.primary,
                marginBottom: '12px'
              }}>
                Would you recommend this school to other parents?
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setWouldRecommend(true)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: wouldRecommend === true ? colors.accent.green : colors.background.white,
                    color: wouldRecommend === true ? 'white' : colors.text.primary,
                    border: `2px solid ${wouldRecommend === true ? colors.accent.green : colors.border.light}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setWouldRecommend(false)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: wouldRecommend === false ? '#FFA07A' : colors.background.white,
                    color: wouldRecommend === false ? 'white' : colors.text.primary,
                    border: `2px solid ${wouldRecommend === false ? '#FFA07A' : colors.border.light}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  No
                </button>
              </div>
            </div>

            {/* Detailed Comments */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: colors.text.primary,
                marginBottom: '12px'
              }}>
                Share your detailed experience (optional)
              </label>
              <textarea
                value={detailedComments}
                onChange={(e) => setDetailedComments(e.target.value)}
                placeholder="What would you like other parents to know about this school?"
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  fontSize: '14px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border.light}`,
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                maxLength={1000}
              />
              <div style={{ 
                fontSize: '12px',
                color: colors.text.secondary,
                marginTop: '4px',
                textAlign: 'right'
              }}>
                {detailedComments.length}/1000
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{ 
                padding: '12px',
                background: '#FEE',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertCircle size={18} color="#D32F2F" />
                <span style={{ color: '#D32F2F', fontSize: '14px' }}>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '16px',
                background: colors.accent.coral,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Send size={20} />
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContributePage;
